import {
  lazy,
  Suspense,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useSyncExternalStore,
} from 'react';
import { flushSync } from 'react-dom';
import { Header } from '@/components/layout/Header';
import { Sidebar } from '@/components/layout/Sidebar';
import { MobileSidebar } from '@/components/layout/MobileSidebar';
import {
  ShortcutsPanel,
  ToastProvider,
  ErrorBoundary,
  useToast,
  OfflineBanner,
  GuidanceBanner,
} from '@/components/ui';
import {
  ProductProvider,
  useProduct,
  SideProvider,
  useSide,
  EditorProvider,
  useEditorSafe,
  AIProvider,
  LibraryProvider,
  useLibrary,
  CartProvider,
  AuthProvider,
  SyncProvider,
  useAuth,
} from '@/context';
import { useCreditPurchaseConfirmation } from '@/features/credits';
import { ProductSelectionScreen } from '@/features/productSelection';
// Lazy-loaded feature components
const ManualEditor = lazy(() =>
  import('@/features/manual').then((m) => ({ default: m.ManualEditor }))
);
const ManualPreview = lazy(() =>
  import('@/features/manual').then((m) => ({ default: m.ManualPreview }))
);
const LibraryTabs = lazy(() =>
  import('@/features/library').then((m) => ({ default: m.LibraryTabs }))
);
const ProjectsGallery = lazy(() =>
  import('@/features/library').then((m) => ({ default: m.ProjectsGallery }))
);
const DesignsGallery = lazy(() =>
  import('@/features/library').then((m) => ({ default: m.DesignsGallery }))
);
const AutoSavesGallery = lazy(() =>
  import('@/features/library').then((m) => ({ default: m.AutoSavesGallery }))
);
const CommunityGallery = lazy(() =>
  import('@/features/library').then((m) => ({ default: m.CommunityGallery }))
);
const FolderDetail = lazy(() =>
  import('@/features/library').then((m) => ({ default: m.FolderDetail }))
);
const CreateFolderModal = lazy(() =>
  import('@/features/library').then((m) => ({ default: m.CreateFolderModal }))
);
const ShareDesignModal = lazy(() =>
  import('@/features/library').then((m) => ({ default: m.ShareDesignModal }))
);
const ShareLinkModal = lazy(() =>
  import('@/features/library').then((m) => ({ default: m.ShareLinkModal }))
);
import { loadCommunityDesignToEditor } from '@/services/communityService';
import type { CommunityDesign, CommunityFolder } from '@/types';
import { useHotkeys } from '@/hooks/useHotkeys';
import { useCloudSync } from '@/hooks/useCloudSync';
import { useFocusMode, useSwipeGesture, useHapticFeedback } from '@/hooks';
import { products } from '@/data/products';
import type { StoredDesign, StoredProject } from '@/services/storage';
import type {
  AutoSavePair,
  AutoSaveLoadMode,
} from '@/features/library/components/AutoSaveCard/AutoSaveCard.types';

// Lazy-loaded components (not needed on initial render)
const CartModal = lazy(() => import('@/features/cart').then((m) => ({ default: m.CartModal })));
const OrderConfirmation = lazy(() =>
  import('@/features/cart').then((m) => ({ default: m.OrderConfirmation }))
);
const OrderHistoryModal = lazy(() =>
  import('@/features/cart').then((m) => ({ default: m.OrderHistoryModal }))
);
const AIEditor = lazy(() => import('@/features/ai').then((m) => ({ default: m.AIEditor })));
const CanvasEditor = lazy(() =>
  import('@/features/canvas').then((m) => ({ default: m.CanvasEditor }))
);
const NotFoundPage = lazy(() =>
  import('@/features/legal').then((m) => ({ default: m.NotFoundPage }))
);
const SupportPage = lazy(() =>
  import('@/features/legal').then((m) => ({ default: m.SupportPage }))
);
const PrivacyPage = lazy(() =>
  import('@/features/legal').then((m) => ({ default: m.PrivacyPage }))
);
const TermsPage = lazy(() => import('@/features/legal').then((m) => ({ default: m.TermsPage })));
const AboutPage = lazy(() => import('@/features/legal').then((m) => ({ default: m.AboutPage })));
const FaqPage = lazy(() => import('@/features/legal').then((m) => ({ default: m.FaqPage })));
const HowItWorksPage = lazy(() =>
  import('@/features/legal').then((m) => ({ default: m.HowItWorksPage }))
);
const FeedbackPage = lazy(() =>
  import('@/features/feedback').then((m) => ({ default: m.FeedbackPage }))
);
import styles from './App.module.css';
import type {
  Mode,
  SubCategory,
  ManualSubCategory,
  AISubCategory,
  CanvasSubCategory,
} from '@/types';
import type { Orientation } from '@/types/orientation.types';

/** sessionStorage key used by EditorContext to persist orientation */
const EDITOR_ORIENTATION_KEY = 'editor_orientation';

/** Read initial orientation from sessionStorage (mirrors EditorContext initialization) */
function getStoredOrientation(): Orientation {
  try {
    const saved = sessionStorage.getItem(EDITOR_ORIENTATION_KEY);
    if (saved === 'landscape') return 'landscape';
  } catch {
    /* ignore */
  }
  return 'portrait';
}

/** Fallback UI shown while lazy-loaded components are being fetched */
function LoadingFallback() {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
      }}
    >
      Loading…
    </div>
  );
}

// Hash routing for legal pages and feedback (works outside main app providers)
const LEGAL_PAGES = ['support', 'privacy', 'terms', 'about', 'faq', 'how-it-works'] as const;
type LegalPage = (typeof LEGAL_PAGES)[number];

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type HashRoute =
  | { page: LegalPage }
  | { page: 'feedback'; orderId: string }
  | { page: 'not-found' }
  | null;

let lastRoute: HashRoute = null;

function getHashRoute(): HashRoute {
  const hash = window.location.hash;

  let next: HashRoute;

  if (!hash || hash === '#' || hash === '#/') {
    next = null;
  } else {
    const legalMatch = hash.match(/^#\/([\w-]+)$/);
    if (legalMatch && LEGAL_PAGES.includes(legalMatch[1] as LegalPage)) {
      next = { page: legalMatch[1] as LegalPage };
    } else {
      const feedbackMatch = hash.match(/^#\/feedback\/([^/]+)$/);
      const feedbackId = feedbackMatch?.[1];
      if (feedbackId && UUID_REGEX.test(feedbackId)) {
        next = { page: 'feedback', orderId: feedbackId };
      } else {
        next = { page: 'not-found' };
      }
    }
  }

  // Return cached reference if structurally equal (useSyncExternalStore requires stable refs)
  if (JSON.stringify(next) === JSON.stringify(lastRoute)) {
    return lastRoute;
  }
  lastRoute = next;
  return next;
}

function subscribeToHash(callback: () => void) {
  window.addEventListener('hashchange', callback);
  return () => window.removeEventListener('hashchange', callback);
}

function useHashRoute(): HashRoute {
  return useSyncExternalStore(subscribeToHash, getHashRoute, () => null);
}

function navigateToPage(page: string) {
  if (page) {
    window.location.hash = `#/${page}`;
  } else {
    // Remove hash and go back to app
    history.replaceState(null, '', window.location.pathname + window.location.search);
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  }
}

// Default sub-categories for each mode
const defaultSubCategories: Record<Mode, SubCategory> = {
  library: 'browse',
  manual: 'canvas',
  ai: 'template',
  canvas: 'background',
};

// localStorage key for mode persistence
const MODE_STORAGE_KEY = 'engraving-pwa-mode';
// sessionStorage key for sub-category persistence (survives auth redirects)
const SUBCATEGORY_STORAGE_KEY = 'engraving-pwa-subcategory';

// Valid modes for localStorage validation
const VALID_MODES: Mode[] = ['library', 'manual', 'ai', 'canvas'];

/**
 * Read mode from localStorage with validation
 * Returns 'ai' if stored value is invalid or missing
 */
function getStoredMode(): Mode {
  try {
    const stored = localStorage.getItem(MODE_STORAGE_KEY);
    if (stored && VALID_MODES.includes(stored as Mode)) {
      return stored as Mode;
    }
  } catch {
    // localStorage may be unavailable (private browsing, etc.)
  }
  return 'ai';
}

/**
 * Save mode to localStorage
 */
function saveMode(mode: Mode): void {
  try {
    localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    // localStorage may be unavailable (private browsing, etc.)
  }
}

function AppContent() {
  const { selectedProduct, selectProduct, clearSelection } = useProduct();
  const { activeSide, flipSide } = useSide();
  const [activeMode, setActiveMode] = useState<Mode>(getStoredMode);
  const [activeSubCategory, setActiveSubCategory] = useState<SubCategory>(() => {
    // Restore sub-category from sessionStorage (survives auth redirects)
    try {
      const stored = sessionStorage.getItem(SUBCATEGORY_STORAGE_KEY);
      if (stored) return stored as SubCategory;
    } catch {
      // sessionStorage may be unavailable
    }
    return defaultSubCategories[getStoredMode()];
  });
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showCart, setShowCart] = useState(false);
  // Register openCart callback so CartProvider (above AppContent) can trigger it
  openCartRef.current = useCallback(() => setShowCart(true), []);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [showOrderHistory, setShowOrderHistory] = useState(false);
  const [isAIGenerating, setIsAIGenerating] = useState(false);
  const [orientation, setOrientation] = useState<Orientation>(getStoredOrientation);
  // Ref to the EditorContext setOrientation — populated by OrientationBridge
  const editorSetOrientationRef = useRef<((o: Orientation) => void) | null>(null);
  const editorHasUserLayersRef = useRef<(() => boolean) | null>(null);

  // Persist sub-category to sessionStorage for auth redirect recovery
  useEffect(() => {
    try {
      sessionStorage.setItem(SUBCATEGORY_STORAGE_KEY, activeSubCategory);
    } catch {
      // sessionStorage may be unavailable
    }
  }, [activeSubCategory]);

  // Focus mode for mobile preview area
  const focusMode = useFocusMode();

  // Haptic feedback for swipe gestures
  const haptic = useHapticFeedback();

  // Enable background cloud sync (silent, runs on app launch + every 5min)
  useCloudSync();

  // Toast notifications
  const toast = useToast();

  // Handle credit purchase confirmation
  const creditPurchaseState = useCreditPurchaseConfirmation();

  // Show toast for credit purchase results
  useEffect(() => {
    if (creditPurchaseState.isConfirmed) {
      toast.showSuccess('Credits purchased successfully!');
    }
    if (creditPurchaseState.error) {
      toast.showError(`Credit purchase failed: ${creditPurchaseState.error}`);
    }
  }, [creditPurchaseState.isConfirmed, creditPurchaseState.error, toast]);

  // Check for payment_intent or session_id in URL and show order confirmation modal
  // Skip if this is a credit purchase (handled by useCreditPurchaseConfirmation)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const creditsPurchased = urlParams.get('credits_purchased');

    // Only show order confirmation for product orders, not credit purchases
    if (
      (urlParams.get('payment_intent') || urlParams.get('session_id')) &&
      creditsPurchased !== 'true'
    ) {
      setShowOrderConfirmation(true);
    }
  }, []);

  // Handle OAuth errors in URL (from failed OAuth redirect)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (error) {
      console.error('[App] OAuth error:', error, errorDescription);
      toast.showError(errorDescription || 'Sign in failed. Please try again.');
      // Clean up URL without reloading
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, [toast]);

  const handleModeChange = useCallback((mode: Mode) => {
    setActiveMode(mode);
    setActiveSubCategory(defaultSubCategories[mode]);
    saveMode(mode); // Persist to localStorage
  }, []);

  // Mode order for swipe navigation (Library ↔ Manual ↔ AI ↔ Canvas)
  const modeOrder: Mode[] = useMemo(() => ['library', 'manual', 'ai', 'canvas'], []);

  // Swipe gesture handlers for mode switching on mobile
  const handleSwipeLeft = useCallback(() => {
    const currentIndex = modeOrder.indexOf(activeMode);
    const nextMode = modeOrder[currentIndex + 1];
    if (nextMode) {
      haptic.medium();
      handleModeChange(nextMode);
    }
  }, [activeMode, haptic, handleModeChange, modeOrder]);

  const handleSwipeRight = useCallback(() => {
    const currentIndex = modeOrder.indexOf(activeMode);
    const prevMode = modeOrder[currentIndex - 1];
    if (prevMode) {
      haptic.medium();
      handleModeChange(prevMode);
    }
  }, [activeMode, haptic, handleModeChange, modeOrder]);

  // Swipe gesture ref for controls area (only on mobile)
  const swipeRef = useSwipeGesture<HTMLElement>(
    { onSwipeLeft: handleSwipeLeft, onSwipeRight: handleSwipeRight },
    { enabled: focusMode.isMobile, threshold: 60, maxVertical: 80 }
  );

  // Auto-scroll to template panel on mobile when entering AI mode
  useEffect(() => {
    if (activeMode === 'ai' && focusMode.isMobile) {
      const controlsEl = swipeRef.current;
      if (controlsEl) {
        requestAnimationFrame(() => {
          controlsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }
  }, [activeMode, focusMode.isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShowShortcuts = useCallback(() => {
    setShowShortcuts(true);
  }, []);

  const handleCloseShortcuts = useCallback(() => {
    setShowShortcuts(false);
  }, []);

  const handleOpenCart = useCallback(() => {
    setShowCart(true);
  }, []);

  const handleCloseCart = useCallback(() => {
    setShowCart(false);
  }, []);

  const handleCloseOrderConfirmation = useCallback(() => {
    setShowOrderConfirmation(false);
  }, []);

  const handleOpenOrderHistory = useCallback(() => {
    setShowOrderHistory(true);
  }, []);

  const handleCloseOrderHistory = useCallback(() => {
    setShowOrderHistory(false);
  }, []);

  const handleOrientationChange = useCallback(
    (newOrientation: Orientation) => {
      if (newOrientation === orientation) return;
      const hasUserLayers = editorHasUserLayersRef.current;
      if (hasUserLayers && hasUserLayers()) {
        const confirmed = window.confirm(
          'Changing orientation will clear your current design. Continue?'
        );
        if (!confirmed) return;
      }
      setOrientation(newOrientation);
      editorSetOrientationRef.current?.(newOrientation);
    },
    [orientation]
  );

  // Global hotkeys (available everywhere)
  useHotkeys({
    onShowShortcuts: handleShowShortcuts,
    onFlipSide: flipSide,
  });

  // Auth is now optional - users can use the app without signing in
  // Auth prompts are shown only when needed (generate, profile, checkout, etc.)

  // Show product selection if no product is selected
  if (!selectedProduct) {
    return (
      <div className={styles.app}>
        <Header
          onShowShortcuts={handleShowShortcuts}
          onOpenCart={handleOpenCart}
          onShowOrderHistory={handleOpenOrderHistory}
        />
        <main className={styles.selectionMain}>
          <ProductSelectionScreen />
        </main>
        <ShortcutsPanel isOpen={showShortcuts} onClose={handleCloseShortcuts} />
        {showCart && (
          <Suspense fallback={<LoadingFallback />}>
            <CartModal isOpen={showCart} onClose={handleCloseCart} />
          </Suspense>
        )}
        {showOrderConfirmation && (
          <Suspense fallback={<LoadingFallback />}>
            <OrderConfirmation
              isOpen={showOrderConfirmation}
              onClose={handleCloseOrderConfirmation}
              onViewOrderHistory={handleOpenOrderHistory}
            />
          </Suspense>
        )}
        {showOrderHistory && (
          <Suspense fallback={<LoadingFallback />}>
            <OrderHistoryModal isOpen={showOrderHistory} onClose={handleCloseOrderHistory} />
          </Suspense>
        )}
        <OfflineBanner />
      </div>
    );
  }

  // Render content that may need EditorProvider
  const renderMainContent = () => (
    <>
      {/* Mobile Sidebar - Mode selection on mobile only */}
      {focusMode.isMobile && (
        <MobileSidebar activeMode={activeMode} onModeChange={handleModeChange} />
      )}

      {/* Sidebar - Desktop/Tablet only (Hero Forge style two-panel) */}
      <Sidebar
        activeMode={activeMode}
        activeSubCategory={activeSubCategory}
        onModeChange={handleModeChange}
        onSubCategoryChange={setActiveSubCategory}
      />

      {/* Preview Area - Center, always visible while selecting options */}
      <section className={styles.previewArea} aria-label="Product preview">
        {/* All modes now share the same canvas via EditorContext */}
        <Suspense fallback={null}>
          <ManualPreview isManualMode={activeMode === 'manual'} activeMode={activeMode} />
        </Suspense>
      </section>

      {/* Controls Area - Right panel with editing options */}
      {/* swipeRef enables horizontal swipe gestures for mode switching on mobile */}
      <section ref={swipeRef} className={styles.controlsArea} aria-label="Design controls">
        <div className={styles.controlsContent}>
          {/* Library Mode */}
          {activeMode === 'library' && (
            <Suspense fallback={<LoadingFallback />}>
              <LibraryContent
                onGoToManual={() => handleModeChange('manual')}
                onGoToAI={() => handleModeChange('ai')}
              />
            </Suspense>
          )}
          {/* Manual Mode */}
          {activeMode === 'manual' && (
            <Suspense fallback={<LoadingFallback />}>
              <ManualEditor
                activeCategory={activeSubCategory as ManualSubCategory}
                onCategoryChange={setActiveSubCategory}
              />
            </Suspense>
          )}
          {/* AI Mode */}
          {activeMode === 'ai' && (
            <Suspense fallback={<LoadingFallback />}>
              <AIEditor
                activeCategory={activeSubCategory as AISubCategory}
                onCategoryChange={setActiveSubCategory}
                onSwitchToLibrary={() => handleModeChange('library')}
                onSwitchToManual={() => handleModeChange('manual')}
                onGeneratingChange={setIsAIGenerating}
              />
            </Suspense>
          )}
          {/* Canvas Mode — freeform AI canvas with Background / Stickers toggle + Enhance */}
          {activeMode === 'canvas' && (
            <Suspense fallback={<LoadingFallback />}>
              <CanvasEditor
                activeCategory={activeSubCategory as CanvasSubCategory}
                onCategoryChange={setActiveSubCategory}
                onGeneratingChange={setIsAIGenerating}
              />
            </Suspense>
          )}
        </div>
      </section>
    </>
  );

  // Show design editor once a product is selected
  return (
    <div className={styles.app}>
      <Header
        selectedProduct={selectedProduct}
        products={products}
        onProductChange={selectProduct}
        onGoHome={clearSelection}
        onShowShortcuts={handleShowShortcuts}
        onOpenCart={handleOpenCart}
        onShowOrderHistory={handleOpenOrderHistory}
        disableProductChange={isAIGenerating}
        orientation={orientation}
        onOrientationChange={handleOrientationChange}
      />

      <EditorProvider product={selectedProduct} activeSide={activeSide}>
        <OrientationBridge
          onMount={(setOrientationFn, hasUserLayersFn) => {
            editorSetOrientationRef.current = setOrientationFn;
            editorHasUserLayersRef.current = hasUserLayersFn;
          }}
        />
        <ModeChangeHandler activeMode={activeMode} />
        <main className={styles.main}>
          {activeMode === 'manual' && <EditorHotkeyHandler />}
          {activeMode === 'ai' ? (
            <AIProvider activeSide={activeSide}>{renderMainContent()}</AIProvider>
          ) : activeMode === 'library' ? (
            <LibraryProvider>{renderMainContent()}</LibraryProvider>
          ) : (
            renderMainContent()
          )}
        </main>
      </EditorProvider>

      <ShortcutsPanel isOpen={showShortcuts} onClose={handleCloseShortcuts} />
      {showCart && (
        <Suspense fallback={<LoadingFallback />}>
          <CartModal isOpen={showCart} onClose={handleCloseCart} />
        </Suspense>
      )}
      {showOrderConfirmation && (
        <Suspense fallback={<LoadingFallback />}>
          <OrderConfirmation
            isOpen={showOrderConfirmation}
            onClose={handleCloseOrderConfirmation}
            onViewOrderHistory={handleOpenOrderHistory}
          />
        </Suspense>
      )}
      {showOrderHistory && (
        <Suspense fallback={<LoadingFallback />}>
          <OrderHistoryModal isOpen={showOrderHistory} onClose={handleCloseOrderHistory} />
        </Suspense>
      )}
      <OfflineBanner />
    </div>
  );
}

/**
 * Component that renders Library tabs and galleries
 * Must be inside LibraryProvider and EditorProvider
 */
function LibraryContent({
  onGoToManual,
  onGoToAI,
}: {
  onGoToManual?: () => void;
  onGoToAI?: () => void;
}) {
  const {
    selectDesign,
    activeTab,
    setActiveTab,
    selectedFolder,
    selectFolder,
    viewingFolderDetail,
    setViewingFolderDetail,
  } = useLibrary();
  const editor = useEditorSafe();
  const { selectedProduct, selectProduct } = useProduct();
  const { setActiveSide } = useSide();

  // Modal states for community sharing
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showShareDesignModal, setShowShareDesignModal] = useState(false);
  const [showShareLinkModal, setShowShareLinkModal] = useState(false);
  const [designToShare, setDesignToShare] = useState<StoredDesign | null>(null);
  const [folderToShare, setFolderToShare] = useState<CommunityFolder | null>(null);

  const handleDesignClick = useCallback(
    async (design: StoredDesign) => {
      // Set the selected design in library context (for preview display)
      selectDesign(design);

      // Load the design into the editor canvas
      if (editor) {
        try {
          await editor.loadFromLibrary(design.id);
          console.warn('[LibraryContent] Loaded design into canvas:', design.id);
        } catch (error) {
          console.error('[LibraryContent] Failed to load design:', error);
        }
      }
    },
    [selectDesign, editor]
  );

  const handleProjectClick = useCallback(
    async (project: StoredProject) => {
      if (!editor) return;

      try {
        // Switch product if the project belongs to a different product
        if (selectedProduct && project.productId !== selectedProduct.id) {
          selectProduct(project.productId);
        }

        await editor.loadSnapshot(project.snapshot);
        console.warn('[LibraryContent] Loaded project into canvas:', project.id);
      } catch (error) {
        console.error('[LibraryContent] Failed to load project:', error);
      }
    },
    [editor, selectedProduct, selectProduct]
  );

  const handleAutoSaveClick = useCallback(
    async (pair: AutoSavePair, mode: AutoSaveLoadMode) => {
      if (!editor) return;

      try {
        if (mode === 'front' && pair.front) {
          flushSync(() => setActiveSide('front'));
          await editor.loadSnapshot(pair.front.snapshot);
        } else if (mode === 'back' && pair.back) {
          flushSync(() => setActiveSide('back'));
          await editor.loadSnapshot(pair.back.snapshot);
        } else if (mode === 'both') {
          if (pair.front) {
            flushSync(() => setActiveSide('front'));
            await editor.loadSnapshot(pair.front.snapshot);
          }
          if (pair.back) {
            flushSync(() => setActiveSide('back'));
            await editor.loadSnapshot(pair.back.snapshot);
          }
          // Return to front view
          setActiveSide('front');
        }
      } catch (error) {
        console.error('[LibraryContent] Failed to load auto-save:', error);
      }
    },
    [editor, setActiveSide]
  );

  const handleCommunityDesignClick = useCallback(
    async (design: CommunityDesign) => {
      // Load the community design into the editor canvas
      if (editor) {
        try {
          // Convert community design to StoredDesign format
          const storedDesign = await loadCommunityDesignToEditor(design);
          selectDesign(storedDesign);

          // Load the design layers if available, otherwise load from blob
          if (storedDesign.frontLayers) {
            await editor.loadSnapshot(storedDesign.frontLayers);
          }
          console.warn('[LibraryContent] Loaded community design into canvas:', design.id);
        } catch (error) {
          console.error('[LibraryContent] Failed to load community design:', error);
        }
      }
    },
    [selectDesign, editor]
  );

  const handleBackToFolders = useCallback(() => {
    selectFolder(null);
    setViewingFolderDetail(false);
  }, [selectFolder, setViewingFolderDetail]);

  // Community sharing handlers
  const handleShareDesign = useCallback((design: StoredDesign) => {
    setDesignToShare(design);
    setShowShareDesignModal(true);
  }, []);

  const handleCloseShareDesignModal = useCallback(() => {
    setShowShareDesignModal(false);
    setDesignToShare(null);
  }, []);

  const handleShareSuccess = useCallback(
    (_sharedDesign: CommunityDesign, folder: CommunityFolder) => {
      setShowShareDesignModal(false);
      setDesignToShare(null);
      setFolderToShare(folder);
      setShowShareLinkModal(true);
    },
    []
  );

  const handleCloseShareLinkModal = useCallback(() => {
    setShowShareLinkModal(false);
    setFolderToShare(null);
  }, []);

  const handleOpenCreateFolder = useCallback(() => {
    setShowCreateFolderModal(true);
  }, []);

  const handleCloseCreateFolder = useCallback(() => {
    setShowCreateFolderModal(false);
  }, []);

  const handleFolderCreated = useCallback((_folder: CommunityFolder) => {
    setShowCreateFolderModal(false);
    // If we were sharing a design, the ShareDesignModal will refresh its folder list
  }, []);

  // Show folder detail view when a folder is selected
  if (activeTab === 'community' && viewingFolderDetail && selectedFolder) {
    return (
      <FolderDetail
        folder={selectedFolder}
        onBack={handleBackToFolders}
        onDesignClick={handleCommunityDesignClick}
      />
    );
  }

  return (
    <>
      <GuidanceBanner screenId="library-mode" />
      <LibraryTabs activeTab={activeTab} onTabChange={setActiveTab} />
      {activeTab === 'projects' && (
        <ProjectsGallery onProjectClick={handleProjectClick} onGoToManual={onGoToManual} />
      )}
      {activeTab === 'designs' && (
        <DesignsGallery
          onDesignClick={handleDesignClick}
          onShareDesign={handleShareDesign}
          onGoToAI={onGoToAI}
        />
      )}
      {activeTab === 'autosaves' && <AutoSavesGallery onAutoSaveClick={handleAutoSaveClick} />}
      {activeTab === 'community' && (
        <CommunityGallery onFolderClick={selectFolder} onCreateClick={handleOpenCreateFolder} />
      )}

      {/* Community sharing modals */}
      <CreateFolderModal
        isOpen={showCreateFolderModal}
        onClose={handleCloseCreateFolder}
        onSuccess={handleFolderCreated}
      />
      {designToShare && (
        <ShareDesignModal
          isOpen={showShareDesignModal}
          design={designToShare}
          onClose={handleCloseShareDesignModal}
          onSuccess={handleShareSuccess}
          onCreateFolder={handleOpenCreateFolder}
        />
      )}
      {folderToShare && (
        <ShareLinkModal
          isOpen={showShareLinkModal}
          folder={folderToShare}
          onClose={handleCloseShareLinkModal}
        />
      )}
    </>
  );
}

/**
 * Bridge component that exposes EditorContext's setOrientation and hasUserLayers
 * to the parent AppContent which renders outside EditorProvider.
 * Must be rendered directly inside EditorProvider.
 */
function OrientationBridge({
  onMount,
}: {
  onMount: (setOrientationFn: (o: Orientation) => void, hasUserLayersFn: () => boolean) => void;
}) {
  const editor = useEditorSafe();

  useEffect(() => {
    if (editor) {
      onMount(editor.setOrientation, editor.hasUserLayers);
    }
    // onMount is stable (defined inline as JSX prop), but we only need this on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  return null;
}

/**
 * Component that handles editor-specific hotkeys
 * Must be inside EditorProvider
 * Only rendered when activeMode === 'manual'
 */
function EditorHotkeyHandler() {
  const editor = useEditorSafe();

  useHotkeys(
    {
      onToolChange: editor?.setTool,
      onUndo: editor?.undo,
      onRedo: editor?.redo,
      onDelete: () => {
        if (editor?.state.activeLayerId) {
          editor.deleteLayer(editor.state.activeLayerId);
        }
      },
      onDuplicate: () => {
        if (editor?.state.activeLayerId) {
          editor.duplicateLayer(editor.state.activeLayerId);
        }
      },
      onDeselect: () => editor?.setActiveLayer(null),
      onZoomIn: () => editor?.setZoom((editor.state.canvas.zoom || 100) + 10),
      onZoomOut: () => editor?.setZoom((editor.state.canvas.zoom || 100) - 10),
      onResetView: editor?.resetView,
      onToggleGrid: editor?.toggleGrid,
    },
    { enabled: !!editor }
  );

  return null;
}

/**
 * Component that resets drawing tools when leaving Manual mode
 * Prevents pen/eraser from being active when on Library/AI tabs
 */
function ModeChangeHandler({ activeMode }: { activeMode: Mode }) {
  const editor = useEditorSafe();
  const prevMode = useRef(activeMode);

  useEffect(() => {
    // If leaving manual mode, reset drawing tools to 'select'
    if (prevMode.current === 'manual' && activeMode !== 'manual') {
      const currentTool = editor?.state.activeTool;
      if (editor && (currentTool === 'pen' || currentTool === 'eraser')) {
        editor.setTool('select');
      }
    }
    prevMode.current = activeMode;
  }, [activeMode, editor]);

  return null;
}

// Shared ref so App can pass openCart to CartProvider before AppContent mounts
const openCartRef: { current: (() => void) | null } = { current: null };

/** Bridge component that reads userId from AuthContext and passes it to SyncProvider */
function SyncProviderBridge({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  return <SyncProvider userId={user?.id ?? null}>{children}</SyncProvider>;
}

export function App() {
  const route = useHashRoute();

  // Legal pages render outside all providers (no auth required)
  if (route?.page === 'support')
    return (
      <Suspense fallback={<LoadingFallback />}>
        <SupportPage onNavigate={navigateToPage} />
      </Suspense>
    );
  if (route?.page === 'privacy')
    return (
      <Suspense fallback={<LoadingFallback />}>
        <PrivacyPage onNavigate={navigateToPage} />
      </Suspense>
    );
  if (route?.page === 'terms')
    return (
      <Suspense fallback={<LoadingFallback />}>
        <TermsPage onNavigate={navigateToPage} />
      </Suspense>
    );
  if (route?.page === 'about')
    return (
      <Suspense fallback={<LoadingFallback />}>
        <AboutPage onNavigate={navigateToPage} />
      </Suspense>
    );
  if (route?.page === 'faq')
    return (
      <Suspense fallback={<LoadingFallback />}>
        <FaqPage onNavigate={navigateToPage} />
      </Suspense>
    );
  if (route?.page === 'how-it-works')
    return (
      <Suspense fallback={<LoadingFallback />}>
        <HowItWorksPage onNavigate={navigateToPage} />
      </Suspense>
    );

  // 404 page for invalid hash routes
  if (route?.page === 'not-found') {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <NotFoundPage onNavigate={navigateToPage} />
      </Suspense>
    );
  }

  // Feedback page needs auth but not product/cart providers
  if (route?.page === 'feedback') {
    return (
      <ErrorBoundary featureName="feedback">
        <ToastProvider position="top-right">
          <AuthProvider>
            <Suspense fallback={<LoadingFallback />}>
              <FeedbackPage orderId={route.orderId} />
            </Suspense>
          </AuthProvider>
        </ToastProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary featureName="the application">
      <ToastProvider position="top-right">
        <AuthProvider>
          <SyncProviderBridge>
            <ProductProvider>
              <SideProvider>
                <CartProvider openCart={() => openCartRef.current?.()}>
                  <AppContent />
                </CartProvider>
              </SideProvider>
            </ProductProvider>
          </SyncProviderBridge>
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}
